import { formatSignedCentsPerLb } from "./callForm";
import type { Nomination, RosterSupplier } from "./types";

export type AnnouncementStatus = Nomination["announcementStatusCode"];

export interface NominationCard {
  supplierId: number;
  supplierName: string;
  announcementStatus: AnnouncementStatus;
  latestNomination: Nomination | null;
}

export interface NominationBoard {
  cards: NominationCard[];
  checkedSupplierCount: number;
  lowestAnnouncedCents: number | null;
  highestAnnouncedCents: number | null;
}

export const ANNOUNCEMENT_STATUS_LABELS: Record<AnnouncementStatus, string> = {
  Announced: "Announced",
  NoAnnouncement: "No announcement",
  NotYetChecked: "Not yet checked",
};

function latestNominationBySupplier(nominations: Nomination[]): Map<number, Nomination> {
  const latestBySupplier = new Map<number, Nomination>();
  for (const nomination of nominations) {
    if (nomination.status !== "Active") continue;
    const currentLatest = latestBySupplier.get(nomination.supplierId);
    if (!currentLatest || (nomination.recordedUtc ?? "") > (currentLatest.recordedUtc ?? "")) {
      latestBySupplier.set(nomination.supplierId, nomination);
    }
  }
  return latestBySupplier;
}

export function buildNominationBoard(roster: RosterSupplier[], nominations: Nomination[]): NominationBoard {
  const latestBySupplier = latestNominationBySupplier(nominations);
  const cards: NominationCard[] = [];
  for (const supplier of roster) {
    if (!supplier.isTracked || supplier.supplierId === undefined) continue;
    const latestNomination = latestBySupplier.get(supplier.supplierId) ?? null;
    cards.push({
      supplierId: supplier.supplierId,
      supplierName: supplier.supplierName ?? "",
      announcementStatus: latestNomination?.announcementStatusCode ?? "NotYetChecked",
      latestNomination,
    });
  }
  const announcedAmounts = cards
    .map((card) => (card.announcementStatus === "Announced" ? card.latestNomination?.amountCentsPerLb : null))
    .filter((amount): amount is number => amount !== null && amount !== undefined);
  return {
    cards,
    checkedSupplierCount: cards.filter((card) => card.announcementStatus !== "NotYetChecked").length,
    lowestAnnouncedCents: announcedAmounts.length ? Math.min(...announcedAmounts) : null,
    highestAnnouncedCents: announcedAmounts.length ? Math.max(...announcedAmounts) : null,
  };
}

export function nominationAmountLabel(centsPerLb: number): string {
  return centsPerLb === 0 ? "Rollover" : formatSignedCentsPerLb(centsPerLb);
}

export function nominationRangeLabel(board: NominationBoard): string {
  const { lowestAnnouncedCents, highestAnnouncedCents } = board;
  if (lowestAnnouncedCents === null || highestAnnouncedCents === null) return "None announced";
  if (lowestAnnouncedCents === highestAnnouncedCents) return nominationAmountLabel(lowestAnnouncedCents);
  return `${nominationAmountLabel(lowestAnnouncedCents)} to ${nominationAmountLabel(highestAnnouncedCents)}`;
}
